(() => {
  const TRANSITION_TYPE_KEY = 'custom-transition-type';
  const disableTransitions = shouldDisableCrossDocumentViewTransitions();

  if (disableTransitions) {
    disableCrossDocumentViewTransitions();
  }

  const renderBlocker = document.getElementById(
    'view-transition-render-blocker'
  );

  if (
    prefersReducedMotion() ||
    isLowPowerDevice() ||
    disableTransitions
  ) {
    renderBlocker?.remove();
  } else {
    const timeout = Math.max(0, 1800 - performance.now());

    window.setTimeout(() => {
      renderBlocker?.remove();
    }, timeout);
  }

  const runWhenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1500 });
    } else {
      window.setTimeout(callback, 0);
    }
  };

  window.addEventListener('pageswap', (event) => {
    const viewTransition = event.viewTransition;

    if (shouldSkipViewTransition(viewTransition)) {
      safelySkipTransition(viewTransition);
      return;
    }

    const cancelTransition = () => {
      safelySkipTransition(viewTransition);
    };

    document.addEventListener('pointerdown', cancelTransition, {
      once: true
    });

    document.addEventListener('keydown', cancelTransition, {
      once: true
    });

    document
      .querySelectorAll(
        '[data-view-transition-type]:not([data-view-transition-triggered])'
      )
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const triggeredElement = document.querySelector(
      '[data-view-transition-triggered]'
    );

    const transitionType = triggeredElement?.getAttribute(
      'data-view-transition-type'
    );

    if (transitionType) {
      setTransitionType(viewTransition, transitionType);
      safeSessionSet(TRANSITION_TYPE_KEY, transitionType);
    } else {
      setTransitionType(viewTransition, 'page-navigation');
      safeSessionRemove(TRANSITION_TYPE_KEY);
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const viewTransition = event.viewTransition;

    if (shouldSkipViewTransition(viewTransition)) {
      safelySkipTransition(viewTransition);
      cleanTransitionState();
      return;
    }

    const customTransitionType = safeSessionGet(
      TRANSITION_TYPE_KEY
    );

    if (!customTransitionType) {
      setTransitionType(viewTransition, 'page-navigation');
      return;
    }

    setTransitionType(viewTransition, customTransitionType);

    try {
      if (
        viewTransition.finished &&
        typeof viewTransition.finished.then === 'function'
      ) {
        await viewTransition.finished;
      }
    } catch (error) {
      /*
       * A rejected finished promise is expected when a transition
       * is skipped, interrupted or aborted.
       */
      safelySkipTransition(viewTransition);
    } finally {
      setTransitionType(viewTransition, 'page-navigation');

      runWhenIdle(() => {
        cleanTransitionState();
      });
    }
  });

  window.addEventListener('pageshow', (event) => {
    /*
     * Back-forward cache restores can retain transition-related
     * attributes from the previous document state.
     */
    if (event.persisted) {
      cleanTransitionState();
      document.documentElement.style.removeProperty('view-transition-name');
      document.body?.style.removeProperty('view-transition-name');
    }
  });

  window.addEventListener('pagehide', () => {
    /*
     * Never leave an old custom transition type stored if navigation
     * is interrupted.
     */
    window.setTimeout(() => {
      safeSessionRemove(TRANSITION_TYPE_KEY);
    }, 3000);
  });

  function shouldSkipViewTransition(viewTransition) {
    return (
      !isUsableViewTransition(viewTransition) ||
      disableTransitions ||
      isLowPowerDevice() ||
      prefersReducedMotion()
    );
  }

  function isUsableViewTransition(viewTransition) {
    /*
     * Do not use `instanceof ViewTransition`.
     * Some WebViews expose transition events without exposing a
     * usable global ViewTransition constructor.
     */
    return Boolean(
      viewTransition &&
      typeof viewTransition === 'object' &&
      typeof viewTransition.skipTransition === 'function'
    );
  }

  function safelySkipTransition(viewTransition) {
    if (!viewTransition) return;

    try {
      if (typeof viewTransition.skipTransition === 'function') {
        viewTransition.skipTransition();
      }
    } catch (error) {
      /*
       * Ignore transitions that have already completed or were
       * previously skipped.
       */
    }
  }

  function setTransitionType(viewTransition, type) {
    const types = viewTransition?.types;

    if (
      !types ||
      typeof types.clear !== 'function' ||
      typeof types.add !== 'function'
    ) {
      return;
    }

    try {
      types.clear();
      types.add(type);
    } catch (error) {
      safelySkipTransition(viewTransition);
    }
  }

  function cleanTransitionState() {
    safeSessionRemove(TRANSITION_TYPE_KEY);

    document
      .querySelectorAll(
        '[data-view-transition-type], [data-view-transition-triggered]'
      )
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
        element.removeAttribute('data-view-transition-triggered');
      });
  }

  function safeSessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage may be unavailable in private or embedded browsers.
    }
  }

  function safeSessionRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage may be unavailable in private or embedded browsers.
    }
  }

  function shouldDisableCrossDocumentViewTransitions(
    userAgent = navigator.userAgent
  ) {
    const ua = userAgent || '';

    const androidWebView =
      /\bAndroid\b/i.test(ua) &&
      /;\s?wv\)/i.test(ua);

    const knownInAppBrowser =
      /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(
        ua
      );

    return androidWebView || knownInAppBrowser;
  }

  function disableCrossDocumentViewTransitions() {
    const styleId = 'disable-cross-document-view-transitions';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent =
        '@view-transition { navigation: none; }';

      (document.head || document.documentElement).appendChild(style);
    }

    document
      .getElementById('view-transition-render-blocker')
      ?.remove();

    cleanTransitionState();
  }

  function prefersReducedMotion() {
    return window
      .matchMedia('(prefers-reduced-motion: reduce)')
      .matches;
  }

  function isLowPowerDevice() {
    const cores = Number(navigator.hardwareConcurrency);
    const memory = Number(navigator.deviceMemory);

    const lowCoreCount =
      Number.isFinite(cores) && cores > 0 && cores <= 2;

    const lowMemory =
      Number.isFinite(memory) && memory > 0 && memory <= 2;

    return lowCoreCount || lowMemory;
  }
})();